import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, FileCode2, ExternalLink, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

const PLANNING_STATUS_STYLE = {
  Permitted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Conditions: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Rejected: "bg-rose-100 text-rose-800 border-rose-200",
  Withdrawn: "bg-rose-100 text-rose-800 border-rose-200",
  Undecided: "bg-amber-100 text-amber-800 border-amber-200",
  Referred: "bg-amber-100 text-amber-800 border-amber-200",
  Unresolved: "bg-amber-100 text-amber-800 border-amber-200",
  Unknown: "bg-muted text-muted-foreground border-border",
};

function formatDMY(isoDate) {
  if (!isoDate) return "";
  const parts = String(isoDate).slice(0, 10).split("-");
  if (parts.length !== 3) return isoDate;
  const [y, m, d] = parts;
  return `${d}/${m}/${y}`;
}

/**
 * Agent/Master admin property edit -> standalone "Planning Applications"
 * CTA. Separate from "Estimate Price" — just nearby planning activity for
 * this listing's postcode, via PlanIt (planit.org.uk).
 */
export function PlanningApplicationsModal({ propertyId, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [apps, setApps] = useState(null);
  const [postcode, setPostcode] = useState("");
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(
        `${API}/property-admin/property/${propertyId}/planning-applications`,
        { withCredentials: true }
      );
      setApps(data.applications || []);
      setPostcode(data.postcode || "");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to load planning applications";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    if (!open || !propertyId) return;
    setApps(null);
    load();
  }, [open, propertyId, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="planning-applications-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <FileCode2 className="h-5 w-5 text-emerald-600 shrink-0" />
            Nearby Planning Applications
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            Real planning activity near {postcode || "this property"}, via PlanIt (planit.org.uk).
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex flex-col items-center justify-center py-10 sm:py-12 gap-3" data-testid="planning-applications-loading">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground text-center px-4">Looking up nearby planning applications…</p>
          </div>
        )}

        {!loading && error && (
          <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md p-4" data-testid="planning-applications-error">
            {error}
          </div>
        )}

        {!loading && apps && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button type="button" variant="outline" size="sm" className="text-xs" onClick={load} data-testid="planning-applications-refresh-btn">
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                Refresh
              </Button>
            </div>
            {apps.length > 0 ? (
              <div className="space-y-1.5" data-testid="planning-applications-list">
                {apps.map((a, i) => (
                  <a
                    key={i}
                    href={a.link || undefined}
                    target="_blank"
                    rel="noreferrer"
                    className="block border rounded-md px-2.5 sm:px-3 py-2 text-sm hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="truncate min-w-0 flex-1 font-medium">{a.address}</span>
                      <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${PLANNING_STATUS_STYLE[a.status] || PLANNING_STATUS_STYLE.Unknown}`}>
                        {a.status}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{a.description}</p>
                    <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-1">
                      {formatDMY(a.date)}
                      {a.link && <ExternalLink className="h-3 w-3 ml-1" />}
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic" data-testid="planning-applications-empty">
                No nearby planning applications were found for this postcode.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PlanningApplicationsModal;
